// S2 Task 2-A — 단일 권한 레이어 (SSOT).
//
// 순수 함수(hook 아님) — 호출부가 이미 보유한 isClientMode/email로 계산.
//   → App.jsx "새 hooks 금지" 규칙 회피(useAuth 결과를 그대로 넘김).
// 노출/권한 값은 feature-inventory.md 권한 컬럼 + Task 0 35-가드 맵 기준(현행 동작 핀).
//
// 3역할: client(공유링크 /client/{uuid}) / AE(opinno.co.kr allowed_users) / admin(ADMIN_EMAILS).
//   - client mode면 email이 admin이어도 client로 취급(공유 뷰어는 항상 광고주 권한).
//   - AE = 인증됐고 client도 admin도 아닌 나머지(묵시적).
import { isAdmin as isAdminEmail } from "./auth.js";

export function derivePermissions({ isClientMode = false, email = "" } = {}) {
  const client = !!isClientMode;
  const admin = !client && isAdminEmail(email);
  const ae = !client && !admin;

  return {
    role: client ? "client" : admin ? "admin" : "ae",
    isClient: client,
    isAE: ae,
    isAdmin: admin,

    // ── 노출(타일/메뉴) — client 숨김 ──
    showGallery: !client, // 소재 갤러리
    showMonitoring: !client, // 모니터링 (MonitoringTab 내부 client/AE 표기는 死코드 — 사이드바 필터로 도달 불가, S3 정리 백로그)
    showGear: !client, // 설정 그룹(tags/goals/media_mgmt/brand_editor)
    showGa4Custom: !client, // GA4 커스텀 조회
    showCollection: !client, // 데이터 수집 모음(2-B)
    showAiChat: !client, // 리포트 AI 질의 채팅 — 조회 기능이라 canEditConfig(편집)와 분리(B6)
    showAdminMonitoring: admin, // 관리자 모니터링 — admin 전용(AE도 X)
    // C5: 근거 표면(성과 근거 탭) — 3역할 전부 노출. 역할은 밀도만 바꾸고 권한은 바꾸지 않는다
    // (admin/AE=근거 원장 · client=의사결정 브리프). 첫 슬라이스는 전 역할 읽기 전용이라
    // 별도 쓰기 capability 키를 만들지 않는다. 셸 타일과 App 라우트 가드가 같은 값을 소비.
    showEvidenceWorkspace: true,

    // ── 편집/관리 capability — client 차단 ──
    // 지표설정·예산/목표편집·태그매핑·운영액션로그·B9편집·엑셀·PPT 등 config/마스터 편집 전반.
    canEditConfig: !client,
    canManageBrand: !client, // 브랜드 설정/추가/공유링크/등록/로그아웃
    // 선언적 미러(B6: 의도적 미배선) — 실제 차단은 brandStorage.js·safeStorage.js(B3a backstop)가
    // isClientMode()를 직접 호출하는 자립형 레이어. 여기에 배선하면 호출부 입력 오류가
    // backstop을 뚫는 경로가 생기므로 배선 금지. matrix 테스트가 이 선언의 진리값을 핀.
    canWriteLocalStorage: !client,
    // S3-4(승인 보강 3): 재수집 트리거 — admin·AE ✓ / client ✗.
    // showMonitoring 재사용 금지(client 모니터링 노출 백로그와 권한 연쇄 차단) — 전용 키.
    canTriggerCollect: !client,

    // ── 리포트 서브탭 — client=분석만 ──
    reportClientAnalysisOnly: client,

    // ── AE ↔ admin 세부 ──
    canEditThreshold: admin, // 이상치 임계값 편집(AE는 read-only)
    canConfigWebhook: admin, // Slack Webhook(AE는 멘션/무음만)
    canRegisterBrandFullscreen: admin, // 브랜드 0개 전체화면 등록(AE는 모달 +버튼)
    // Task 3 사후 승인: /kit 데모 전용 키 — showAdminMonitoring(모니터링 정책)과 분리해
    // 정책 변경이 /kit 접근을 흔들지 않게 함. 현재 값은 동일하게 admin.
    canViewKitDemo: admin,
  };
}
